import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { identity, getConfig, ApiError } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Stack, Card, Title, Button, Text, Group } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, Textarea, Checkbox } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { useWallet } from '../../../data/useWallet';
import { useCredits } from '../../../lib/credits';
import { formatDate } from '../../../lib/format';

/**
 * Export everything (owners and admins) and close the account (owners), both
 * owner's decisions of 2 October 2026. Closing was "email support".
 */
export function AccountCards() {
  const { user } = useAuth();
  const role = user?.role;
  const admin = role == null || identity.isOrgAdmin(role);
  if (!admin) return null;
  return (
    <Stack gap="md">
      <ExportCard />
      {(role == null || role === 'owner') && <CloseCard />}
    </Stack>
  );
}

function ExportCard() {
  const t = useT();
  const exp = useMutation({
    mutationFn: () => identity.createExportLink(),
    onSuccess: (r) => {
      // The browser saves the zip itself; nothing large passes through the page.
      window.location.assign(`${getConfig().apiBaseUrl.replace(/\/$/, '')}${r.data.url}`);
      notifySuccess(t('settings.export.started'), t('settings.export.title'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.export.title'))
  });
  return (
    <Card withBorder>
      <Stack gap="sm">
        <Title order={4}>{t('settings.export.title')}</Title>
        <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.export.body')}</Text>
        <Group><Button variant="default" loading={exp.isPending} onClick={() => exp.mutate()}>{t('settings.export.button')}</Button></Group>
      </Stack>
    </Card>
  );
}

function CloseCard() {
  const t = useT();
  const { user, refresh } = useAuth();
  const { data: wallet } = useWallet();
  const credits = useCredits();
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [refund, setRefund] = useState(false);
  const [mismatch, setMismatch] = useState(false);
  const closesAt = user?.org.closes_at ?? null;
  const bought = wallet?.data?.purchased_minor ?? 0;

  const close = useMutation({
    mutationFn: () => identity.closeAccount({ confirm_name: name.trim(), reason: reason.trim() || undefined, refund_unused_credits: refund }),
    onSuccess: async (r) => {
      await refresh();
      notifySuccess(t('settings.close.requested', { date: formatDate(r.data.closes_at ?? '') }), t('settings.close.title'));
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'CONFIRMATION_MISMATCH') { setMismatch(true); return; }
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.close.title'));
    }
  });
  const keep = useMutation({
    mutationFn: () => identity.keepAccount(),
    onSuccess: async () => { await refresh(); notifySuccess(t('settings.close.kept'), t('settings.close.title')); },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.close.title'))
  });

  if (closesAt) {
    return (
      <Card withBorder style={{ borderColor: 'var(--rr-color-danger)' }}>
        <Stack gap="sm">
          <Title order={4} c="red">{t('settings.close.title')}</Title>
          <Text role="bodySmall">{t('settings.close.pending', { date: formatDate(closesAt) })}</Text>
          <Group><Button loading={keep.isPending} onClick={() => keep.mutate()}>{t('settings.close.keep')}</Button></Group>
        </Stack>
      </Card>
    );
  }

  return (
    <Card withBorder style={{ borderColor: 'var(--rr-color-danger)' }}>
      <Stack gap="md">
        <Title order={4} c="red">{t('settings.close.title')}</Title>
        <Alert color="red" variant="light">
          <Stack gap={6}>
            <Text fz="sm">{t('settings.close.what')}</Text>
            <Text fz="sm">{t('settings.close.kept30')}</Text>
          </Stack>
        </Alert>
        {bought > 0 && (
          <Checkbox
            checked={refund}
            onChange={(checked) => setRefund(checked)}
            label={t('settings.close.refund', { amount: credits(bought) })}
          />
        )}
        <Textarea label={t('settings.close.reason')} description={t('settings.close.reasonHint')} value={reason}
          onChange={(e) => setReason(e.currentTarget.value)} maxLength={1000} autosize minRows={2} />
        <TextInput label={t('settings.close.confirm', { name: user?.org.name ?? '' })} value={name}
          onChange={(e) => { setName(e.currentTarget.value); setMismatch(false); }}
          error={mismatch ? t('settings.close.mismatch') : undefined} autoComplete="off" />
        <Group justify="flex-end">
          <Button color="red" disabled={name.trim().length === 0} loading={close.isPending} onClick={() => close.mutate()}>
            {t('settings.close.button')}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
